/**
 * Who may do what.
 *
 * This module is the single source of truth for authorization *decisions*. It
 * is deliberately dependency-free and pure so that the exact same functions
 * run in three places:
 *
 *   • the browser, to decide what to draw;
 *   • Cloud Functions, to decide what to allow;
 *   • the test suite, to prove the two agree.
 *
 * The Security Rules express the same policy in their own language and are the
 * final word — nothing here is trusted by the database. But when this file and
 * the rules disagree, that is a bug in one of them, and the tests in
 * shared/authz.test.ts exist to catch it.
 *
 * Nothing in this file reads a clock, opens a connection, or looks anything
 * up. Callers pass in the facts; this decides.
 */

export const ADMIN_EMAIL = 'samuel.ray@slr.family';
export const ALLOWED_DOMAIN = 'slr.family';

export type Role = 'admin' | 'student';

/**
 * The identity an authorization question is asked about.
 *
 * `emailVerified` is not decoration. Firebase will happily mint a token for an
 * account asserting any address; only a verified one is evidence.
 */
export interface Principal {
  uid: string;
  email: string;
  emailVerified: boolean;
}

/** Everything a permission check may need to know about the world. */
export interface AccessContext {
  /** True when this principal has an enrollment document for the semester. */
  enrolled: boolean;
  /** Semester window, as instants. */
  startAt: Date;
  endAt: Date;
  /** Set by "Reopen Course"; extends Admin's write access, never a student's. */
  reopenedUntil?: Date | null;
  now: Date;
}

export function normalizeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

/** A verified account on the family domain. The floor for everything else. */
export function isFamilyAccount(p: Principal | null | undefined): boolean {
  if (!p || !p.emailVerified) return false;
  const email = normalizeEmail(p.email);
  // Anchored: `evil@slr.family.example.com` must not pass.
  return new RegExp(`^[^@\\s]+@${ALLOWED_DOMAIN.replace('.', '\\.')}$`).test(email);
}

export function isAdmin(p: Principal | null | undefined): boolean {
  return isFamilyAccount(p) && normalizeEmail(p!.email) === ADMIN_EMAIL;
}

/** Every other verified family account. Grants nothing on its own. */
export function isStudent(p: Principal | null | undefined): boolean {
  return isFamilyAccount(p) && !isAdmin(p);
}

export function roleOf(p: Principal | null | undefined): Role | null {
  if (isAdmin(p)) return 'admin';
  if (isStudent(p)) return 'student';
  return null;
}

/**
 * The window in which a semester exists for a student.
 *
 * Before it: the semester is not "upcoming", it is absent. No card, no
 * countdown, no greyed-out row. After it: gone again. Admin is unaffected.
 */
export function semesterInSession(ctx: Pick<AccessContext, 'startAt' | 'endAt' | 'now'>): boolean {
  return ctx.now >= ctx.startAt && ctx.now <= ctx.endAt;
}

/** May this principal see the semester at all? */
export function canViewSemester(p: Principal | null | undefined, ctx: AccessContext): boolean {
  if (isAdmin(p)) return true;
  if (!isStudent(p)) return false;
  return ctx.enrolled && semesterInSession(ctx);
}

/**
 * May Admin still change this semester's content?
 *
 * A semester that has ended is an archive. Reopening it is a deliberate act
 * with an expiry, and it moves this line only — students remain outside the
 * date window regardless.
 */
export function canEditSemesterContent(
  p: Principal | null | undefined,
  ctx: Pick<AccessContext, 'endAt' | 'reopenedUntil' | 'now'>
): boolean {
  if (!isAdmin(p)) return false;
  if (ctx.now <= ctx.endAt) return true;
  return !!ctx.reopenedUntil && ctx.now < ctx.reopenedUntil;
}

/** True once a semester has ended and has not been reopened. */
export function isArchived(ctx: Pick<AccessContext, 'endAt' | 'reopenedUntil' | 'now'>): boolean {
  if (ctx.now <= ctx.endAt) return false;
  return !(ctx.reopenedUntil && ctx.now < ctx.reopenedUntil);
}

/** The shape of anything that can be draft, published, or published later. */
export interface PublishableLike {
  state: 'draft' | 'published';
  publishAt?: Date | null;
}

/**
 * Is this item live for a reader right now?
 *
 * A scheduled announcement is `draft` until the scheduler flips it, but the
 * `publishAt` guard is kept anyway: it costs nothing and means a hand-edited
 * or clock-skewed record still cannot leak early.
 */
export function isLiveForStudents(item: PublishableLike, now: Date): boolean {
  if (item.state !== 'published') return false;
  if (item.publishAt && item.publishAt > now) return false;
  return true;
}

/**
 * The one function every content list should call.
 *
 * Admin sees drafts; a student sees an item only if the semester is open to
 * them and the item is live. Passing `viewAsStudent` makes Admin subject to
 * the student answer — that is all "Test as Student" is, and routing it
 * through here is what makes the preview honest.
 */
export function canViewContent(
  p: Principal | null | undefined,
  item: PublishableLike,
  ctx: AccessContext,
  viewAsStudent = false
): boolean {
  if (isAdmin(p) && !viewAsStudent) return true;
  if (!isFamilyAccount(p)) return false;
  if (!ctx.enrolled || !semesterInSession(ctx)) return false;
  return isLiveForStudents(item, ctx.now);
}

/**
 * Capabilities the interface asks about by name.
 *
 * Naming them keeps `email === ADMIN_EMAIL` out of components entirely: a
 * screen asks `can(user, 'manage:enrollment')`, not who the user is.
 */
export type Capability =
  | 'admin:console'
  | 'admin:search'
  | 'admin:activity'
  | 'admin:deliveries'
  | 'manage:semesters'
  | 'manage:content'
  | 'manage:enrollment'
  | 'manage:groups'
  | 'test-as-student';

export function can(p: Principal | null | undefined, capability: Capability): boolean {
  // Every capability defined so far is Admin's. The enumeration exists so that
  // adding a second privileged role later is an edit here, not a search.
  void capability;
  return isAdmin(p);
}
